/**
 * Campaign utilities and constants
 * Shared across all campaign sub-components
 */

// Available theme colors for campaigns
export const CAMPAIGN_THEME_OPTIONS = [
  { key: 'teal', name: 'Teal', color: '#0D9488', gradient: 'from-teal-500 to-teal-600', textColor: 'text-teal-500 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-900/20', border: 'border-teal-200 dark:border-teal-700' },
  { key: 'orange', name: 'Orange', color: '#D97706', gradient: 'from-amber-500 to-amber-600', textColor: 'text-amber-500 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-700' },
  { key: 'purple', name: 'Purple', color: '#7C3AED', gradient: 'from-violet-500 to-violet-600', textColor: 'text-violet-500 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/20', border: 'border-violet-200 dark:border-violet-700' },
  { key: 'black', name: 'Black', color: '#1a1a1a', gradient: 'from-gray-600 to-gray-800', textColor: 'text-gray-500 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-800/50', border: 'border-gray-400 dark:border-gray-600' },
  { key: 'blue', name: 'Blue', color: '#2563EB', gradient: 'from-blue-500 to-blue-600', textColor: 'text-blue-500 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-700' },
  { key: 'green', name: 'Green', color: '#16A34A', gradient: 'from-green-500 to-green-600', textColor: 'text-green-500 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-700' },
  { key: 'red', name: 'Red', color: '#DC2626', gradient: 'from-red-500 to-red-600', textColor: 'text-red-500 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-700' },
  { key: 'pink', name: 'Pink', color: '#DB2777', gradient: 'from-pink-500 to-pink-600', textColor: 'text-pink-500 dark:text-pink-400', bg: 'bg-pink-50 dark:bg-pink-900/20', border: 'border-pink-200 dark:border-pink-700' },
  { key: 'indigo', name: 'Indigo', color: '#4F46E5', gradient: 'from-indigo-500 to-indigo-600', textColor: 'text-indigo-500 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/20', border: 'border-indigo-200 dark:border-indigo-700' },
  { key: 'amber', name: 'Amber', color: '#F59E0B', gradient: 'from-amber-400 to-amber-500', textColor: 'text-amber-500 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-700' },
];

// Legacy theme mapping for backwards compatibility (campaigns 1-4)
export const LEGACY_CAMPAIGN_THEMES = {
  1: { gradient: 'from-teal-500 to-teal-600', color: 'text-teal-500 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-900/20', border: 'border-teal-200 dark:border-teal-700' },
  2: { gradient: 'from-amber-500 to-amber-600', color: 'text-amber-500 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-700' },
  3: { gradient: 'from-violet-500 to-violet-600', color: 'text-violet-500 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/20', border: 'border-violet-200 dark:border-violet-700' },
  4: { gradient: 'from-gray-600 to-gray-800', color: 'text-gray-500 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-800/50', border: 'border-gray-400 dark:border-gray-600' }
};

// Legacy campaign names
export const LEGACY_CAMPAIGN_NAMES = {
  1: 'Founding Members',
  2: 'Island Energy',
  3: 'Grand Opening',
  4: 'Biz Card Grand Opening'
};

// Production domain for QR codes
export const PRODUCTION_DOMAIN = 'https://bulaccinocafe.com';

/**
 * Get theme by key (with fallback to teal)
 */
export const getThemeByKey = (key) => {
  return CAMPAIGN_THEME_OPTIONS.find(t => t.key === key) || CAMPAIGN_THEME_OPTIONS[0];
};

/**
 * Get theme for a campaign - handles both legacy and dynamic campaigns
 */
export const getCampaignTheme = (campaignId, themeKey) => {
  // Legacy campaigns 1-4 use hardcoded themes
  if (LEGACY_CAMPAIGN_THEMES[campaignId]) {
    return LEGACY_CAMPAIGN_THEMES[campaignId];
  }
  // New campaigns use theme from database or fallback
  const themeOption = getThemeByKey(themeKey || 'teal');
  return {
    gradient: themeOption.gradient,
    color: themeOption.textColor,
    bg: themeOption.bg,
    border: themeOption.border
  };
};

/**
 * Get resolved theme from report/campaign data with fallbacks
 */
export const getResolvedTheme = (campaignId, reportCampaigns, allCampaigns) => {
  const reportCampaign = reportCampaigns?.find(c => c.campaign_id === campaignId);
  const dbCampaign = allCampaigns?.find(c => c.campaign_id === campaignId);
  const themeKey = reportCampaign?.theme || dbCampaign?.theme || 'teal';
  return getCampaignTheme(campaignId, themeKey);
};
